<script setup>
import {computed, inject, ref, watch} from 'vue';
import {trans} from 'laravel-vue-i18n';
import axios from 'axios';
import {Loader} from '@googlemaps/js-api-loader';
import {MarkerClusterer} from '@googlemaps/markerclusterer';

const displayConfig = inject('displayConfig');
const product = inject('product');

let map;
let currentZoom = 14;

const loader = new Loader({
    apiKey: 'AIzaSyDR_EiQhVFnTUzGJiK_7JXZTBy3Qnx-LjQ',
    version: 'weekly',
    language: 'fr',
    region: 'FR',
    libraries: ['geometry']
});

loader.load().then(async () => {
    if (document.getElementById('map')) {
        const {Map} = await google.maps.importLibrary('maps');
        map = new Map(document.getElementById('map'), {
            center: {lat: 45.9131603, lng: 1.2710793}, // Center of France
            // center: {lat: 48.866667, lng: 2.333333}, // Paris
            zoom: currentZoom,
            minZoom: 11,
            maxZoom: 19,
            disableDefaultUI: true,
            zoomControl: true,
            clickableIcons: false,
        });

        const mapDiv = document.getElementById('map');
        if (mapDiv) {
            mapDiv.setAttribute('tabindex', '15');
            mapDiv.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    const focusable = mapDiv.querySelectorAll('button, [tabindex="0"]');
                    if (focusable.length > 0) {
                        focusable[0].focus();
                        event.preventDefault();
                    }
                }
            });
        }

        // searchAroundCenter();

        map.addListener('dragend', () => {
            // Show the refresh button after dragging the map
            showRefreshBtn.value = true;
        });

        map.addListener('zoom_changed', () => {
            // close all info windows
            closeInfoWindows();

            if (map.getZoom() < currentZoom) {
                // Show the refresh button after zooming out the map
                showRefreshBtn.value = true;
            }
            // Update the current zoom value
            currentZoom = map.getZoom();
        });
    }
});

function searchAroundCenter() {
    showRefreshBtn.value = false;
    const center = map.getCenter();
    coords.value = {lng: center.lng(), lat: center.lat()};
    searchCoordinates(false);
}

const customRenderer = {
    render: function ({count, position}, stats) {
        // use d3-interpolateRgb to interpolate between red and blue
        const color = '#000000';
        // create svg url with fill color
        const svg = window.btoa(`
  <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
    <circle cx="120" cy="120" opacity=".8" r="70" />
    <circle cx="120" cy="120" opacity=".8" r="70" />
  </svg>`);
        // create marker using svg icon
        return new google.maps.Marker({
            position,
            icon: {
                url: `data:image/svg+xml;base64,${svg}`,
                scaledSize: new google.maps.Size(50, 50),
            },
            label: {
                text: String(count),
                color: 'rgba(255,255,255,0.9)',
                fontSize: '12px',
            },
            // adjust zIndex to be above other markers
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
    },
};

const searchOnce = ref(false);
const showMap = ref(false);
const address = ref('');
const errorMessage = ref('');
const loading = ref(false);
const loadingGeolocate = ref(false);
const locations = ref([]);
const coords = ref({lng: 0, lat: 0});
const showRefreshBtn = ref(false);

let markers = [];
let markerClusterer = null;
let openedInfoWindow = null;
const defaultDistance = 10000;
let distance = defaultDistance;

function searchCoordinates(updateZoom) {
    showMap.value = true;
    map.setCenter(coords.value);
    if (updateZoom) {
        map.setZoom(12);
    }
    let url = '/api/search-bookstores?lng=' + coords.value.lng + '&lat=' + coords.value.lat;
    if (product.value.editor_id) {
        url += '&editor_id=' + product.value.editor_id;
    }
    google.maps.event.addListenerOnce(map, 'idle', () => {
        search(url);
    });
}

function searchAddress() {
    let url = '/api/search-bookstores?address=' + encodeURIComponent(address.value);
    if (product.value.editor_id) {
        url += '&editor_id=' + product.value.editor_id;
    }
    search(url)
        .then(() => {
            if (locations.value.length > 0) {
                showMap.value = true;
                coords.value = {lat: locations.value[0].lat, lng: locations.value[0].lng};
                map.setCenter(coords.value);
                // map.setZoom(12);
            }
        });
}

function closeInfoWindows() {
    for (let location of locations.value) {
        location.infoWindow.close();
    }
}

function openInfoWindowOf(location) {
    closeInfoWindows();
    openedInfoWindow = location.infoWindow;
    location.infoWindow.open({
        anchor: location.marker,
        map,
    });
}

function calculateDistance() {
    distance = defaultDistance;
    const bounds = map.getBounds();
    if (bounds) {
        distance = google.maps.geometry.spherical.computeDistanceBetween(bounds.getNorthEast(), bounds.getSouthWest());
    }
}

async function search(url) {
    if (!loading.value) {
        loading.value = true;
        errorMessage.value = '';
        locations.value = [];
        if (markerClusterer) {
            markerClusterer.clearMarkers();
            markerClusterer = null;
        }
        for (let marker of markers) {
            marker.setMap(null);
        }
        markers = [];
        closeInfoWindows();
        // calculate distance and append to URL
        calculateDistance();
        if (distance) {
            url += '&distance=' + distance;
        }
        return await axios
            .get(url)
            .then(response => {
                searchOnce.value = true;
                if (response.status === 200) {
                    locations.value = response.data.locations;
                    // showMap.value = true;
                    // markers
                    for (let location of locations.value) {
                        // create new Marker
                        const marker = new google.maps.Marker({
                            position: {lat: location.lat, lng: location.lng},
                            map,
                            title: getLocationName(location),
                            icon: '/map-pin.svg'
                        });
                        // attach marker to location
                        location.marker = marker;
                        // add to markers array
                        markers.push(marker);
                        // create new infoWindow and attach it to location
                        location.infoWindow = new google.maps.InfoWindow();
                        location.infoWindow.setContent(
                            '<div class="info-window">' +
                            '<div class="name">' + getLocationName(location) + '</div>' +
                            ((location.ADCLI || location.CPCLI || location.LOBDI) ?
                                '<div class="address">' +
                                '<address>' +
                                (location.ADCLI ? '<div>' + location.ADCLI + '</div>' : '') +
                                ((location.CPCLI || location.LOBDI) ?
                                    '<div>' +
                                    '<span>' + location.CPCLI + '&nbsp;</span><span>' + location.LOBDI + '</span>' +
                                    '</div>' : '') +
                                '</address>' +
                                '</div>' : '') +
                            (location.PHONE ? '<div class="phone"><a href="tel:' + location.PHONE + '">tél.: ' + location.PHONE + '</a></div>' : '') +
                            '</div>'
                        );
                        // On click on a marker, open the infoWindow attached to its location
                        marker.addListener('click', () => {
                            openInfoWindowOf(location);
                        });
                        marker.setOptions({
                            clickable: true,
                        });
                    }
                    // Create a marker Clusterer
                    markerClusterer = new MarkerClusterer({
                        markers,
                        map,
                        renderer: customRenderer
                    })
                    ;
                } else {
                    errorMessage.value = trans('Une erreur est survenue.');
                }
            })
            .catch(error => {
                if (error.response) {
                    errorMessage.value = error.response.data.message;
                } else {
                    console.error(error);
                }
            })
            .finally(() => {
                loading.value = false;
            });
    }
}

function clickLocation(location) {
    map.panTo({lat: location.lat, lng: location.lng});
    map.setZoom(map.maxZoom);
    openInfoWindowOf(location);
}

function formatDistance(distanceInMeters) {
    // Définir une valeur limite pour basculer entre mètres et kilomètres
    const kilometerThreshold = 1000;

    // Vérifier si la distance est inférieure à la limite
    if (distanceInMeters < kilometerThreshold) {
        return distanceInMeters.toFixed(0) + ' mètres';
    } else {
        // Convertir la distance en kilomètres avec une seule décimale
        const distanceInKilometers = (distanceInMeters / 1000).toFixed(1);
        return distanceInKilometers + ' kilomètres';
    }
}

function geolocate() {
    loadingGeolocate.value = true;
    loading.value = false;
    errorMessage.value = '';
    locations.value = [];
    navigator.geolocation.getCurrentPosition(
        // Succès : la position est récupérée
        function (position) {
            loadingGeolocate.value = false;
            address.value = '';
            coords.value = {lat: position.coords.latitude, lng: position.coords.longitude};
            searchCoordinates(false);
        },
        // Erreur : impossible de récupérer la position
        function (error) {
            loadingGeolocate.value = false;
            errorMessage.value = trans('Veuillez autoriser la géolocalisation dans votre navigateur ou bien renseignez une adresse dans le champ "Adresse" ci-dessus.');
            console.error(`Erreur de géolocalisation : ${error.message}`);
        }
    );
}

function getLocationName(location) {
    if (location.NOCLI) {
        return location.NOCLI;
    } else if (location.RASOC) {
        const regex = /^(?:[0-9FL]*\s)?(?:ASSO|ASS|ASS|AS|EI|EIRL|ETS|EURL|LIB|MAG|SA|SARL|SASU|SAS|SNC|STE)[\s\.](.*)/gm;

        return location.RASOC.replace(regex, '$1');
    }
}
</script>

<template>
    <div class="bookstores-map">
        <div class="header">
            <h3>{{ $t('Trouver une librairie') }}</h3>
            <div class="switch" :class="{'switch--checked':showMap}" v-if="searchOnce">
                <label for="show-map-cb">{{ $t('Afficher la carte') }}</label>
                <div class="switch-input-wrapper">
                    <div class="track"></div>
                    <div class="input">
                        <input id="show-map-cb"
                               type="checkbox"
                               name="show_map"
                               v-model="showMap"
                               :disabled="loading || loadingGeolocate"
                               tabindex="15"
                        >
                        <div class="thumb"></div>
                    </div>
                </div>
            </div>
        </div>

        <div class="input-address-wrapper">
            <form action="#" @submit.prevent="searchAddress">
                <div class="pin" @click="geolocate"
                     tabindex="15"
                     role="button"
                     :aria-label="trans('Me géolocaliser')"
                     @keydown.enter.prevent="geolocate"
                     @keydown.space.prevent="geolocate">
                    <img src="../../images/locator.svg" alt=""
                         v-show="!loadingGeolocate && displayConfig.theme === 'light'">
                    <img src="../../images/locator-dark.svg" alt=""
                         v-show="!loadingGeolocate && displayConfig.theme === 'dark'">
                    <div class="loading" v-show="loadingGeolocate"></div>
                </div>
                <input type="text"
                       id="address"
                       autocomplete="off"
                       :placeholder="trans('Adresse')"
                       v-model="address"
                       :disabled="loading || loadingGeolocate"
                       :aria-label="trans('Adresse')"
                       tabindex="15">
                <button type="submit"
                        :disabled="loading || loadingGeolocate || address.length === 0"
                        :aria-label="trans('Rechercher')"
                        tabindex="15">
                    <img src="../../images/search.svg" alt="" v-show="!loading">
                    <div class="loading" v-show="loading"></div>
                </button>
            </form>
        </div>

        <div class="error" v-show="errorMessage">{{ errorMessage }}</div>

        <div class="map-wrapper" v-show="showMap">
            <button
                class="refresh"
                :class="{'refresh--show': showRefreshBtn}"
                @click="searchAroundCenter"
                tabindex="15"
            >
                {{ $t('Rechercher dans cette zone') }}
            </button>
            <div id="map"></div>
        </div>

        <div class="locations-wrapper" v-show="showMap">
            <ul v-if="locations.length>0">
                <li v-for="location in locations" @click="clickLocation(location)"
                    tabindex="15"
                    @keydown.enter.prevent="clickLocation(location)"
                    @keydown.space.prevent="clickLocation(location)">
                    <div class="name">{{ getLocationName(location) }}</div>
                    <div v-if="location.ADCLI || location.CPCLI || location.LOBDI" class="address">
                        <address>
                            <div v-if="location.ADCLI">{{ location.ADCLI }}</div>
                            <div v-if="location.CPCLI || location.LOBDI">
                                <span v-if="location.CPCLI">{{ location.CPCLI }}&nbsp;</span><span
                                v-if="location.LOBDI">{{ location.LOBDI }}</span>
                            </div>
                        </address>
                    </div>
                    <div v-if="location.PHONE" class="phone">
                        <a :href="'tel:' + location.PHONE">tél.: {{ location.PHONE }}</a>
                    </div>
                    <div class="distance">{{ formatDistance(location.distanceInMeters) }}</div>
                </li>
            </ul>
            <div v-else-if="searchOnce && locations.length === 0 && !errorMessage && !(loading || loadingGeolocate)"
                 class="info">
                {{ $t('Aucune librairie trouvée autour de votre localisation.') }}<br>
                {{ $t('Élargissez votre recherche en utilisant la carte.') }}
            </div>
        </div>
    </div>
</template>
